/**
 * Course service for fetching level data from the backend API
 */
import api from './api';

class CourseService {
  /**
   * Get all levels
   * @returns {Promise<Array>} Array of level objects
   */
  async getAllCourses() {
    try {
      const response = await api.get('/levels');
      // The new API structure has a 'levels' array
      return response.data.levels || response.data.courses || response.data || [];
    } catch (error) {
      console.error('Error fetching levels:', error);
      throw error;
    }
  }

  /**
   * Get all levels (alias for getAllCourses for backward compatibility)
   * @returns {Promise<Array>} Array of level objects
   */
  async getAllLevels() {
    return this.getAllCourses();
  }

  /**
   * Get course by ID
   * @param {string} courseId - Course ID
   * @returns {Promise<Object>} Course object
   */
  async getCourseById(courseId) {
    try {
      const response = await api.get(`/courses/${courseId}`);
      return response.data;
    } catch (error) {
      console.error(`Error fetching course ${courseId}:`, error);
      throw error;
    }
  }

  /**
   * Get level by level code
   * @param {string} level - Level code (A0, A1, etc.)
   * @returns {Promise<Object>} Level object
   */
  async getCourseByLevel(level) {
    try {
      // Use the correct endpoint for fetching by level code
      const response = await api.get(`/levels/level/${level}`);
      return response.data;
    } catch (error) {
      console.error(`Error fetching level ${level}:`, error);
      throw error;
    }
  }

  /**
   * Generate content access token for course/lesson access
   * @param {string} contentType - Type of content (course, lesson, assessment)
   * @param {string} contentId - ID of the content
   * @param {string} levelCode - Level code (A0, A1, etc.)
   * @returns {Promise<Object>} Access token and metadata
   */
  async generateContentToken(contentType, contentId, levelCode = null) {
    try {
      const payload = {
        content_type: contentType,
        content_id: contentId
      };
      
      if (levelCode) {
        payload.level_code = levelCode;
      }
      
      const response = await api.post('/content/generate-token', payload);
      return response.data;
    } catch (error) {
      console.error('Error generating content token:', error);
      throw error;
    }
  }

  /**
   * Track lesson start
   * @param {Object} lessonData - Lesson tracking data
   * @returns {Promise<Object>} Tracking confirmation
   */
  async trackLessonStart(lessonData) {
    try {
      const response = await api.post('/content/track-lesson-start', lessonData);
      return response.data;
    } catch (error) {
      console.error('Error tracking lesson start:', error);
      throw error;
    }
  }

  /**
   * Save lesson progress/completion
   * @param {Object} progressData - Lesson progress data
   * @returns {Promise<Object>} Save confirmation
   */
  async saveLessonProgress(progressData) {
    try {
      const response = await api.post('/content/save-lesson-progress', progressData);
      return response.data;
    } catch (error) {
      console.error('Error saving lesson progress:', error);
      throw error;
    }
  }

  /**
   * Save section completion
   * @param {Object} sectionData - Section completion data
   * @returns {Promise<Object>} Save confirmation
   */
  async saveSectionProgress(sectionData) {
    try {
      const response = await api.post('/content/save-section-progress', sectionData);
      return response.data;
    } catch (error) {
      console.error('Error saving section progress:', error);
      throw error;
    }
  }
}

// Create and export singleton instance
const courseService = new CourseService();
export default courseService;